import Alpine from 'alpinejs'

const productManager = () => ({
  products: [],
  projectId: null,
  productCounter: 0,
  attrCounter: 0,
  valueCounter: 0,
  skuCounter: 0,

  initWithData(jsonString) {
    const initialData = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString
    this.projectId = initialData.projectId
    this.products = initialData.existingProducts || []

    // Initialize display names for existing SKUs (sorted by attribute order)
    this.products.forEach((product, idx) => {
      if (product.skus && product.attributes) {
        const attrOrder = product.attributes.map(a => a.name)
        product.skus.forEach(sku => {
          // Sort attribute_values by the attribute order defined in product.attributes
          const sortedValues = [...sku.attribute_values].sort((a, b) => {
            const indexA = attrOrder.indexOf(a.attribute_name)
            const indexB = attrOrder.indexOf(b.attribute_name)
            return indexA - indexB
          })
          sku.display_name = sortedValues.map(av => av.value).join(' / ') || sku.name
        })
      }

      // 沒有屬性且沒有可見 SKU 的商品，產生預設 SKU
      const hasValidAttributes = product.attributes && product.attributes.some(a => !a._destroy)
      const hasVisibleSkus = product.skus && product.skus.some(s => !s._destroy)
      if (!hasValidAttributes && !hasVisibleSkus) {
        this.generateSkus(idx)
      }
    })
  },

  addProduct() {
    this.products.push({
      tempId: `new_${this.productCounter++}`,
      name: '',
      attributes: [],
      skus: [],
      _destroy: false
    })
  },

  removeProduct(productIdx) {
    const product = this.products[productIdx]
    if (product.id) {
      product._destroy = true
    } else {
      this.products.splice(productIdx, 1)
    }
  },

  isProductNameDuplicated(productIdx) {
    const product = this.products[productIdx]
    if (!product || product._destroy || !product.name || product.name.trim() === '') {
      return false
    }

    const currentName = product.name.trim().toLowerCase()
    return this.products.some((p, idx) =>
      idx !== productIdx &&
      !p._destroy &&
      p.name &&
      p.name.trim().toLowerCase() === currentName
    )
  },

  isAttrNameDuplicated(productIdx, attrIdx) {
    const product = this.products[productIdx]
    const currentAttr = product.attributes[attrIdx]
    if (!currentAttr || currentAttr._destroy || !currentAttr.name || currentAttr.name.trim() === '') {
      return false
    }

    const currentName = currentAttr.name.trim().toLowerCase()
    return product.attributes.some((attr, idx) =>
      idx !== attrIdx &&
      !attr._destroy &&
      attr.name &&
      attr.name.trim().toLowerCase() === currentName
    )
  },

  validateBeforeSubmit() {
    for (let i = 0; i < this.products.length; i++) {
      const product = this.products[i]
      if (product._destroy) continue

      if (this.isProductNameDuplicated(i)) {
        alert(`商品名稱「${product.name}」重複，請修改後再儲存`)
        return false
      }

      const attrs = product.attributes || []
      const seenNames = new Set()

      for (let j = 0; j < attrs.length; j++) {
        const attr = attrs[j]
        if (attr._destroy) continue

        if (!attr.name || attr.name.trim() === '') {
          alert(`商品「${product.name || '(未命名)'}」有一個屬性尚未填寫名稱`)
          return false
        }

        const normalizedName = attr.name.trim().toLowerCase()
        if (seenNames.has(normalizedName)) {
          alert(`商品「${product.name}」的屬性名稱「${attr.name}」重複`)
          return false
        }
        seenNames.add(normalizedName)

        const activeValues = attr.values.filter(v => !v._destroy)
        if (activeValues.length === 0) {
          alert(`商品「${product.name}」的屬性「${attr.name}」至少需要一個屬性值`)
          return false
        }
      }
    }
    return true
  },

  addAttribute(productIdx) {
    this.products[productIdx].attributes.push({
      tempId: `new_attr_${this.attrCounter++}`,
      name: '',
      values: [],
      _destroy: false
    })
  },

  removeAttribute(productIdx, attrIdx) {
    const product = this.products[productIdx]
    const currentSkuCount = product.skus ? product.skus.filter(s => !s._destroy).length : 0

    if (currentSkuCount > 0) {
      // 模擬移除後的有效屬性
      const remainingValidAttrs = product.attributes.filter((attr, idx) =>
        idx !== attrIdx && !attr._destroy && attr.name &&
        attr.values.filter(v => !v._destroy).length > 0
      )
      const newSkuCount = this._computeSkuCount(remainingValidAttrs)
      const netDeleted = currentSkuCount - newSkuCount

      if (netDeleted > 0 && !confirm(`刪除此屬性將會一併刪除 ${netDeleted} 個相關 SKU，確定要刪除嗎？`)) {
        return
      }
    }

    const attr = product.attributes[attrIdx]
    if (attr.id) {
      attr._destroy = true
    } else {
      product.attributes.splice(attrIdx, 1)
    }
    this.generateSkus(productIdx)
  },

  addAttributeValueFromInput(productIdx, attrIdx) {
    const input = document.getElementById(`new-value-${productIdx}-${attrIdx}`)
    const value = input.value.trim()
    if (!value) return

    const attr = this.products[productIdx].attributes[attrIdx]

    // Check for duplicates
    if (attr.values.some(v => !v._destroy && v.value === value)) {
      alert('此屬性值已存在')
      return
    }

    attr.values.push({
      tempId: `new_val_${this.valueCounter++}`,
      value: value,
      _destroy: false
    })

    input.value = ''
    this.generateSkus(productIdx)
  },

  removeAttributeValue(productIdx, attrIdx, valIdx) {
    const product = this.products[productIdx]
    const currentSkuCount = product.skus ? product.skus.filter(s => !s._destroy).length : 0

    if (currentSkuCount > 0) {
      const attr = product.attributes[attrIdx]
      const remainingValues = attr.values.filter((v, i) => i !== valIdx && !v._destroy)
      const isAttrStillValid = attr.name && remainingValues.length > 0

      // 模擬移除後的有效屬性
      const simulatedValidAttrs = product.attributes.filter((a, i) => {
        if (i === attrIdx) return isAttrStillValid
        return !a._destroy && a.name && a.values.filter(v => !v._destroy).length > 0
      })

      let newSkuCount
      if (simulatedValidAttrs.length === 0) {
        newSkuCount = 1
      } else {
        newSkuCount = simulatedValidAttrs.reduce((acc, a) => {
          if (a === attr) return acc * remainingValues.length
          return acc * a.values.filter(v => !v._destroy).length
        }, 1)
      }

      const netDeleted = currentSkuCount - newSkuCount
      if (netDeleted > 0 && !confirm(`刪除此屬性值將會一併刪除 ${netDeleted} 個相關 SKU，確定要刪除嗎？`)) {
        return
      }
    }

    const val = product.attributes[attrIdx].values[valIdx]
    if (val.id) {
      val._destroy = true
    } else {
      product.attributes[attrIdx].values.splice(valIdx, 1)
    }
    this.generateSkus(productIdx)
  },

  generateSkus(productIdx) {
    const product = this.products[productIdx]
    const validAttributes = product.attributes.filter(attr =>
      !attr._destroy && attr.name && attr.values.filter(v => !v._destroy).length > 0
    )

    // 保留有 id 的舊 SKU（用於追蹤需要刪除的 SKU）
    const oldSkusWithId = (product.skus || []).filter(sku => sku.id)

    if (validAttributes.length === 0) {
      // 無屬性時產生一個預設 SKU
      const defaultSkuKey = ''
      const existingDefault = oldSkusWithId.find(sku => this.getSkuKey(sku.attribute_values) === defaultSkuKey)

      const skuCode = this.generateSkuCode(product.name, [])
      const defaultSku = {
        tempId: existingDefault?.tempId || `new_sku_${this.skuCounter++}`,
        id: existingDefault?.id,
        sku_code: skuCode,
        name: product.name || '預設',
        display_name: '預設',
        inventory: existingDefault?.inventory || 0,
        attribute_values: [],
        _destroy: false
      }

      const deletedSkus = oldSkusWithId
        .filter(sku => this.getSkuKey(sku.attribute_values) !== defaultSkuKey)
        .map(sku => ({ ...sku, _destroy: true }))

      product.skus = [defaultSku, ...deletedSkus]
      return
    }

    // Calculate cartesian product
    const valueArrays = validAttributes.map(attr =>
      attr.values.filter(v => !v._destroy).map(v => ({
        attribute_name: attr.name,
        value: v.value
      }))
    )

    const combinations = this.cartesianProduct(valueArrays)

    // Preserve existing SKU inventory values
    const existingSkuMap = new Map()
    if (product.skus) {
      product.skus.forEach(sku => {
        const key = this.getSkuKey(sku.attribute_values)
        existingSkuMap.set(key, sku)
      })
    }

    // 計算新組合的 key 集合
    const newKeys = new Set(combinations.map(combo =>
      combo.map(c => `${c.attribute_name}:${c.value}`).sort().join('|')
    ))

    // 找出不在新組合中的舊 SKU，標記為刪除
    const deletedSkus = oldSkusWithId
      .filter(sku => !newKeys.has(this.getSkuKey(sku.attribute_values)))
      .map(sku => ({ ...sku, _destroy: true }))

    const newSkus = combinations.map((combo) => {
      const key = combo.map(c => `${c.attribute_name}:${c.value}`).sort().join('|')
      const existing = existingSkuMap.get(key)

      const skuCode = this.generateSkuCode(product.name, combo)
      const displayName = combo.map(c => c.value).join(' / ')

      return {
        tempId: existing?.tempId || `new_sku_${this.skuCounter++}`,
        id: existing?.id,
        sku_code: skuCode,
        name: displayName,
        display_name: displayName,
        inventory: existing?.inventory || 0,
        attribute_values: combo,
        _destroy: false
      }
    })

    // 合併新 SKU 和被刪除的舊 SKU
    product.skus = [...newSkus, ...deletedSkus]
  },

  _computeSkuCount(validAttributes) {
    if (validAttributes.length === 0) return 1 // 預設 SKU
    return validAttributes.reduce((acc, attr) =>
      acc * attr.values.filter(v => !v._destroy).length, 1
    )
  },

  getSkuKey(attributeValues) {
    if (!attributeValues) return ''
    return attributeValues
      .map(av => `${av.attribute_name}:${av.value}`)
      .sort()
      .join('|')
  },

  cartesianProduct(arrays) {
    if (arrays.length === 0) return [[]]
    if (arrays.length === 1) return arrays[0].map(x => [x])

    const result = []
    const rest = this.cartesianProduct(arrays.slice(1))

    arrays[0].forEach(value => {
      rest.forEach(combination => {
        result.push([value, ...combination])
      })
    })

    return result
  },

  generateSkuCode(productName, combo) {
    const prefix = (productName || 'PRODUCT').substring(0, 5).toUpperCase()
    if (combo.length === 0) {
      return `${this.projectId}-${prefix}-DEFAULT`
    }
    const suffix = combo.map(c => c.value.substring(0, 3).toUpperCase()).join('-')
    return `${this.projectId}-${prefix}-${suffix}`
  },

  // Field name helpers for Rails nested attributes
  getProductFieldName(productIdx, field) {
    return `project[products_attributes][${productIdx}][${field}]`
  },

  getSkuFieldName(productIdx, skuIdx, field) {
    return `project[products_attributes][${productIdx}][product_skus_attributes][${skuIdx}][${field}]`
  }
})

Alpine.data('productManager', productManager)
export default productManager
